import type { ReactNode } from "react";

/**
 * The little mock-ups on the widget cards, drawn in markup exactly as the
 * design prototype draws them — no screenshots to keep in sync.
 */

export const GlobalTrackingPreview = (): ReactNode => (
  <>
    <div className="wga-modes">
      <span className="on">Ocean</span>
      <span>Air</span>
      <span>Rail</span>
    </div>
    <div className="wga-gt">
      <div className="wga-gt__r">
        <i className="a" />
        <s />
        <i className="b" />
        <s className="off" />
        <i className="off" />
      </div>
      <div className="wga-gt__r">
        <i className="a" />
        <s className="off" />
        <i className="off" />
        <s className="off" />
        <i className="off" />
      </div>
    </div>
    <div className="wga-gt__f">
      <u />
      <span className="wga-chip">ETA 12 MAY</span>
    </div>
  </>
);

export const ContainerTrackingPreview = (): ReactNode => (
  <>
    <div className="wga-bar">
      <i className="wga-in" />
      <i className="wga-go" />
    </div>
    <div className="wga-tl">
      <i />
      <s />
      <i />
      <s />
      <i />
      <s />
      <i className="now" />
      <s className="off" />
      <i className="off" />
    </div>
    <div className="wga-lbl">
      <u />
      <u />
      <u />
      <u />
      <u />
    </div>
  </>
);

export const AirTrackingPreview = (): ReactNode => (
  <>
    <div className="wga-code">
      <b>AWB 020-7721</b>
      <span className="wga-chip">IN TRANSIT</span>
    </div>
    <div className="wga-arc">
      <em style={{ left: "12%" }} />
      <em style={{ left: "50%" }} />
      <em style={{ left: "88%" }} />
    </div>
  </>
);

export const VesselSchedulesPreview = (): ReactNode => (
  <>
    <div className="wga-sel">
      <span />
      <span />
    </div>
    <div className="wga-rows">
      {[0, 1, 2].map((row) => (
        <div className="wga-row" key={row}>
          <u className="n" />
          <u className="m" />
          <u className="g" />
        </div>
      ))}
    </div>
  </>
);

export const TransitTimePreview = (): ReactNode => (
  <>
    <div className="wga-sel">
      <span />
      <span />
    </div>
    <div className="wga-big">
      <b>23.4</b>
      <span>days</span>
    </div>
    <div className="wga-sub">
      <u />
      <u />
    </div>
  </>
);

export const FreightBookingPreview = (): ReactNode => (
  <div className="wga-split">
    <div className="wga-f">
      <u />
      <u />
      <u />
      <u className="cta" />
    </div>
    <hr />
    <div className="wga-pr">
      <div>
        <u />
        <u className="v" />
      </div>
      <div>
        <u />
        <u className="v" />
      </div>
      <div>
        <u />
        <u className="v" />
      </div>
      <div className="tot">
        <u />
        <u className="v" />
      </div>
    </div>
  </div>
);

export const LoadCalculatorPreview = (): ReactNode => (
  <div className="wga-cont">
    <span className="wga-fill">62%</span>
    <u className="on" style={{ width: "30%", height: "70%" }} />
    <u style={{ width: "16%", height: "46%" }} />
    <u style={{ width: "16%", height: "46%" }} />
    <u style={{ width: "20%", height: "60%" }} />
    <u style={{ width: "14%", height: "34%" }} />
  </div>
);

export const Co2Preview = (): ReactNode => (
  <>
    <div className="wga-co2">
      <b>12,480</b>
      <span>kg CO₂e</span>
    </div>
    <div className="wga-prog">
      <i />
    </div>
    <div className="wga-sub wga-sub--left">
      <u />
      <u />
      <u />
    </div>
  </>
);

export const FreightIntelligencePreview = (): ReactNode => (
  <>
    <div className="wga-ctop">
      <u />
      <span className="wga-chip">+2.34%</span>
    </div>
    <svg className="wga-chart" viewBox="0 0 300 56" preserveAspectRatio="none" aria-hidden="true">
      <polyline
        points="0,42 40,36 75,45 110,26 145,31 180,18 215,30 250,22 292,12"
        fill="none"
        stroke="#1aac44"
        strokeWidth="2"
        strokeLinejoin="round"
        strokeLinecap="round"
      />
      <circle cx="292" cy="12" r="3.5" fill="#1aac44" />
    </svg>
  </>
);

export const QSearchPreview = (): ReactNode => (
  <>
    <div className="wga-ask">
      <span>Best rate Shanghai → Hamburg?</span>
      <i />
    </div>
    <div className="wga-ai">
      <em>AI</em>
      <s />
    </div>
    <div className="wga-ai">
      <em />
      <s className="sh" />
    </div>
  </>
);

export type Widget = {
  /** The catalogue product the widget embeds; name, cube and category come from there. */
  slug: string;
  description: string;
  preview: ReactNode;
};

/**
 * The widget catalogue, in the design's order. A widget is listed on the Web
 * tab only while the catalogue sells its product for web integration.
 */
export const WIDGETS: Widget[] = [
  { slug: "global-tracking", description: "Multimodal search with unified ETA", preview: <GlobalTrackingPreview /> },
  { slug: "container-tracking", description: "Search box + milestone timeline", preview: <ContainerTrackingPreview /> },
  { slug: "air-tracking", description: "AWB lookup with flight legs", preview: <AirTrackingPreview /> },
  { slug: "load-calculator", description: "Cargo input with stuffing plan", preview: <LoadCalculatorPreview /> },
  { slug: "qsearch-ai", description: "AI search that turns queries into bookings", preview: <QSearchPreview /> },
  { slug: "vessel-schedules", description: "Route search with sailing list", preview: <VesselSchedulesPreview /> },
  { slug: "transit-time", description: "Port pair with time estimate", preview: <TransitTimePreview /> },
  {
    slug: "freight-booking-platform",
    description: "Quote form with price breakdown",
    preview: <FreightBookingPreview />,
  },
  { slug: "co2-intelligence", description: "Emissions figure per shipment", preview: <Co2Preview /> },
  { slug: "freight-intelligence", description: "Index chart by trade lane", preview: <FreightIntelligencePreview /> },
];
